/**
 * InvoicesPage — Settings → Billing.
 *
 * The billing-history surface, split out from Subscription: the payment method
 * on file plus the customer's invoices. Razorpay is the merchant of record
 * (we never hold the money, principle 4), so it issues the GST invoices — each
 * row links to Razorpay's hosted invoice for view/download.
 *
 * State-aware: a workspace with no paying subscription (trial / Razorpay not
 * wired) gets a single, intentional "billing starts when you activate" state
 * with a path to the Subscription page — not two half-empty cards. A paying
 * workspace gets the payment-method panel + the invoices table.
 */
import { Alert, StatusPill } from "@flint/ui";
import { Link } from "@tanstack/react-router";
import { Download, ReceiptText, CreditCard, ArrowRight, ShieldCheck } from "lucide-react";
import { PLAN_BY_CODE } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { WorkspaceShell } from "../../workspace/index.ts";
import { SettingsShell } from "../settings-shell.tsx";
import { FormCard } from "../form-card.tsx";

const STATUS_LABEL: Record<string, string> = {
  trialing: "Free trial",
  activation_required: "Activation required",
  active: "Active",
  past_due: "Payment overdue",
  soft_suspended: "Read-only",
  hard_suspended: "Locked",
  trial_expired: "Trial expired",
  on_hold: "On hold",
  canceled: "Canceled",
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  upi: "UPI AutoPay",
  card: "Card",
  netbanking: "Net banking",
  emandate: "Bank mandate",
  nach: "Bank mandate",
};

const INVOICE_TONE: Record<string, "ok" | "warn" | "bad" | "muted"> = {
  paid: "ok",
  issued: "warn",
  partially_paid: "warn",
  expired: "bad",
  cancelled: "muted",
};

function rupees(paise: number, currency = "INR"): string {
  const symbol = currency === "INR" ? "₹" : `${currency} `;
  return `${symbol}${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function InvoicesPage() {
  const q = trpc.billing.listInvoices.useQuery({});
  const data = q.data;
  const planName = data ? (PLAN_BY_CODE[data.plan]?.name ?? data.plan) : "";

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Billing" }]}>
      <SettingsShell
        activeSection="billing"
        pageTitle="Billing"
        pageDescription="Your invoices and the payment method on file."
      >
        {q.error ? (
          <Alert intent="bad" title="Couldn't load billing" description={q.error.message} />
        ) : !data ? (
          <p className="text-sm text-muted">Loading your billing history…</p>
        ) : !data.configured ? (
          <NotSetUp planName={planName} statusLabel={STATUS_LABEL[data.status] ?? data.status} />
        ) : (
          <div className="flex flex-col gap-4">
            <PaymentMethodCard
              planName={planName}
              statusLabel={STATUS_LABEL[data.status] ?? data.status}
              method={data.paymentMethod}
            />
            <InvoicesCard invoices={data.invoices} />
          </div>
        )}
      </SettingsShell>
    </WorkspaceShell>
  );
}

/** Empty/onboarding state: nothing to bill yet — point the user at activation. */
function NotSetUp({ planName, statusLabel }: { planName: string; statusLabel: string }) {
  return (
    <div className="rounded-lg border border-line bg-paper shadow-card">
      <div className="flex flex-col items-center px-6 py-12 text-center">
        <span className="inline-grid h-14 w-14 place-items-center rounded-xl bg-sky-50 text-sky-700">
          <ReceiptText size={26} strokeWidth={1.75} aria-hidden />
        </span>
        <h2 className="mt-4 text-lg font-semibold text-ink">Billing starts when you go live</h2>
        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-ink-2">
          You're on the <span className="font-medium text-ink">{planName}</span> plan ·{" "}
          {statusLabel.toLowerCase()} — there's nothing to pay yet. Activate a paid plan to set up
          automatic payments; your payment method and monthly invoices show up here.
        </p>
        <Link
          to={"/settings/subscription" as never}
          className="mt-5 inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-paper transition-colors hover:bg-ink-2"
        >
          Go to Subscription
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>

      {/* What will appear here — sets expectations without faking data. */}
      <div className="grid grid-cols-1 gap-px border-t border-line bg-line sm:grid-cols-2">
        <PreviewTile
          icon={<CreditCard size={16} aria-hidden />}
          title="Payment method"
          body="Your UPI AutoPay or card on file, charged automatically each cycle."
        />
        <PreviewTile
          icon={<ReceiptText size={16} aria-hidden />}
          title="Invoices"
          body="A downloadable GST invoice for every payment, kept for your records."
        />
      </div>
    </div>
  );
}

function PreviewTile({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3 bg-paper px-5 py-4">
      <span className="mt-0.5 inline-grid h-8 w-8 shrink-0 place-items-center rounded-md bg-bg text-muted ring-1 ring-line">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted">{body}</p>
      </div>
    </div>
  );
}

function PaymentMethodCard({
  planName,
  statusLabel,
  method,
}: {
  planName: string;
  statusLabel: string;
  method: string | null;
}) {
  return (
    <FormCard
      icon={<CreditCard size={14} strokeWidth={2} aria-hidden />}
      title="Payment method"
      description="How your subscription is paid each cycle."
      badge={
        <div className="flex items-center gap-1.5">
          <StatusPill tone="muted" size="xs">
            {planName}
          </StatusPill>
          <StatusPill tone="ok" size="xs">
            {statusLabel}
          </StatusPill>
        </div>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-line bg-bg/40 px-4 py-3.5">
        <div className="flex items-center gap-3">
          <span className="inline-grid h-10 w-10 place-items-center rounded-md bg-paper text-ink-2 ring-1 ring-line">
            <CreditCard size={18} aria-hidden />
          </span>
          <div>
            <p className="text-sm font-semibold text-ink">
              {method ? (PAYMENT_METHOD_LABEL[method] ?? method) : "No payment method on file"}
            </p>
            <p className="mt-0.5 text-xs text-muted">
              {method ? "Auto-debited every cycle · managed by Razorpay" : "Added when you activate a paid plan"}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted">
          <ShieldCheck size={13} aria-hidden /> Secured by Razorpay
        </span>
      </div>
    </FormCard>
  );
}

interface InvoiceRow {
  id: string;
  number: string | null;
  issuedAt: string | null;
  amountPaise: number;
  currency: string;
  status: string;
  downloadUrl: string | null;
}

function InvoicesCard({ invoices }: { invoices: InvoiceRow[] }) {
  return (
    <FormCard
      icon={<ReceiptText size={14} strokeWidth={2} aria-hidden />}
      title="Invoices"
      description="Download any invoice for your records."
      badge={
        invoices.length > 0 ? (
          <StatusPill tone="muted" size="xs">
            {invoices.length}
          </StatusPill>
        ) : undefined
      }
      bodyClassName="p-0"
    >
      {invoices.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <ReceiptText size={24} className="mx-auto text-line-2" aria-hidden />
          <p className="mt-2 text-sm font-medium text-ink">No invoices yet</p>
          <p className="mt-0.5 text-xs text-muted">
            Invoices appear here after your first subscription payment.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-5 py-2.5 font-medium">Invoice</th>
                <th className="px-5 py-2.5 font-medium">Date</th>
                <th className="px-5 py-2.5 text-right font-medium">Amount</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
                <th className="px-5 py-2.5 text-right font-medium">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-bg/50">
                  <td className="px-5 py-3 font-medium text-ink">{inv.number ?? inv.id}</td>
                  <td className="px-5 py-3 text-ink-2">{formatDate(inv.issuedAt)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink">
                    {rupees(inv.amountPaise, inv.currency)}
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill tone={INVOICE_TONE[inv.status] ?? "muted"} size="xs">
                      {inv.status.replace(/_/g, " ")}
                    </StatusPill>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {inv.downloadUrl ? (
                      <a
                        href={inv.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:underline"
                      >
                        <Download size={13} aria-hidden /> Download
                      </a>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </FormCard>
  );
}
